import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  input,
} from '@angular/core';

import { ComparisonRow } from '../report.model';
import { ChartTheme, paletteFor } from './chart-palette';
import {
  AxisTick,
  GridLine,
  PlotMargin,
  bandCentre,
  plotBox,
  round,
  topRoundedBar,
  yTickX,
} from './chart-geometry';
import {
  ChartTooltipService,
  chartWidthSignal,
} from './chart-tooltip.service';

const MARGIN: PlotMargin = { top: 8, right: 8, bottom: 4, left: 8 };
/** Place réservée aux pourcentages de l'axe des ordonnées. */
const AXIS_WIDTH = 44;
const X_AXIS_HEIGHT = 30;
const HEIGHT = 340;
/** Largeur de repli tant que le conteneur n'est pas mesuré. */
const FALLBACK_WIDTH = 636;
/** `barCategoryGap` 22 %, `barGap` 4, `maxBarSize` 24, repris de Recharts. */
const GROUP_FILL = 0.78;
const BAR_GAP = 4;
const MAX_BAR_WIDTH = 24;
const Y_TICKS = [0, 25, 50, 75, 100];

/** Clé de série — deux, et seulement deux. */
type ComparisonSeries = 'student' | 'target';

interface ComparisonBarView {
  key: string;
  subjectId: number;
  series: ComparisonSeries;
  value: number;
  d: string;
  fill: string;
}

/**
 * Comparaison des matières : la note de l'élève face à l'objectif.
 *
 * **Deux séries, pas trois.** Le document d'origine en portait une troisième,
 * « moyenne de classe », retirée ici : un résultat de diagnostic n'appartient
 * qu'à l'élève et ne se compare pas à ses camarades. La décision est inscrite
 * dans le DTO (`ComparisonRow`) et dans `hydrate.js` ; le seul repère extérieur
 * est l'objectif, qui est une barre fixe. La géométrie de groupe du modèle est
 * conservée telle quelle, simplement instanciée avec `s = 2`.
 *
 * Absent en portée « une matière » : comparer une matière à elle-même n'a rien
 * à dire — le modèle ne fournit alors pas de lignes du tout.
 */
@Component({
  selector: 's2e-comparison-chart',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[style.display]': "'block'" },
  template: `
    <svg
      class="chart-svg comparison__svg"
      [attr.width]="width()"
      [attr.height]="height"
      [attr.viewBox]="viewBox()"
      role="img"
      [attr.aria-label]="ariaLabel() || null"
      focusable="false"
      (mouseleave)="tooltip.hide()"
    >
      <g class="chart-grid">
        @for (line of gridLines(); track line.key) {
          <line
            [attr.x1]="line.x1"
            [attr.y1]="line.y1"
            [attr.x2]="line.x2"
            [attr.y2]="line.y2"
            [attr.stroke]="palette().grid"
            stroke-width="1"
          ></line>
        }
      </g>
      <line
        [attr.x1]="baseline().x1"
        [attr.y1]="baseline().y1"
        [attr.x2]="baseline().x2"
        [attr.y2]="baseline().y2"
        [attr.stroke]="palette().axis"
        stroke-width="1"
      ></line>
      <g class="chart-axis chart-axis--y">
        @for (tick of yTicks(); track tick.key) {
          <text
            [attr.x]="tick.x"
            [attr.y]="tick.y"
            [attr.text-anchor]="tick.anchor"
            dominant-baseline="central"
            [attr.fill]="palette().textMuted"
            font-size="12"
          >{{ tick.label }}</text>
        }
      </g>
      <g class="chart-axis chart-axis--x">
        @for (tick of xTicks(); track tick.key) {
          <text
            [attr.x]="tick.x"
            [attr.y]="tick.y"
            text-anchor="middle"
            dominant-baseline="central"
            [attr.fill]="palette().textMuted"
            font-size="12"
          >{{ tick.label }}</text>
        }
      </g>
      <g class="comparison__bars">
        @for (bar of barViews(); track bar.key) {
          <path
            class="comparison__bar"
            [attr.data-subject]="bar.subjectId"
            [attr.data-series]="bar.series"
            [attr.data-value]="bar.value"
            [attr.d]="bar.d"
            [attr.fill]="bar.fill"
            (mouseenter)="enter(bar.subjectId, $event)"
            (mousemove)="tooltip.move($event)"
          ></path>
        }
      </g>
    </svg>
  `,
})
export class ComparisonChartComponent {
  readonly tooltip = inject(ChartTooltipService);

  readonly rows = input.required<readonly ComparisonRow[]>();
  readonly theme = input.required<ChartTheme>();
  readonly rtl = input.required<boolean>();
  /** Libellés des deux séries, déjà traduits. */
  readonly seriesLabels = input.required<{ student: string; target: string }>();

  /** Libellé accessible complet, déjà traduit par la section. */
  readonly ariaLabel = input('');
  /** Mise en forme des pourcentages (graduations et infobulle). */
  readonly percentFormat = input<(value: number) => string>(
    (value) => `${Math.round(value)}%`,
  );

  readonly height = HEIGHT;

  private readonly measured = chartWidthSignal(FALLBACK_WIDTH);

  readonly width = computed(() => this.measured());
  readonly viewBox = computed(() => `0 0 ${this.width()} ${HEIGHT}`);
  readonly palette = computed(() => paletteFor(this.theme()));

  private readonly box = computed(() =>
    plotBox(this.width(), HEIGHT, MARGIN, AXIS_WIDTH, X_AXIS_HEIGHT, this.rtl()),
  );

  /**
   * Couleurs des deux séries — l'élève et l'objectif, jamais un troisième.
   *
   * <p>L'objectif prend l'orangé du trio et non le violet : côte à côte, le
   * bleu et le violet partagent leur teinte et leur clarté, et deux barres
   * voisines se lisent comme une seule série en deux tons. Le bleu et l'orangé
   * sont les deux extrémités du trio ; ils se distinguent au premier coup
   * d'œil, et restent séparés pour les daltonismes courants, où le violet
   * revient vers le bleu.</p>
   *
   * <p>Les hexadécimaux ne sont pas choisis ici : on prend deux rôles de la
   * palette validée, celle-là même qui garantit contraste et séparation.</p>
   */
  private readonly seriesColors = computed(() => {
    const palette = this.palette();
    return { student: palette.categorical[0], target: palette.categorical[1] };
  });

  private yFor(value: number): number {
    const box = this.box();
    return box.bottom - (value / 100) * box.height;
  }

  private readonly ordered = computed(() => {
    const list = this.rows().slice();
    return this.rtl() ? list.reverse() : list;
  });

  private readonly band = computed(() => {
    const count = this.ordered().length;
    return count === 0 ? 0 : this.box().width / count;
  });

  readonly gridLines = computed<GridLine[]>(() => {
    const box = this.box();
    return Y_TICKS.map((value) => {
      const y = round(this.yFor(value));
      return { key: value, x1: round(box.left), y1: y, x2: round(box.right), y2: y };
    });
  });

  readonly baseline = computed(() => {
    const box = this.box();
    const y = round(box.bottom);
    return { x1: round(box.left), y1: y, x2: round(box.right), y2: y };
  });

  readonly yTicks = computed<AxisTick[]>(() => {
    const box = this.box();
    const rtl = this.rtl();
    const format = this.percentFormat();
    return Y_TICKS.map((value) => ({
      key: value,
      x: yTickX(box, rtl),
      y: round(this.yFor(value)),
      label: format(value),
      anchor: rtl ? ('start' as const) : ('end' as const),
    }));
  });

  readonly xTicks = computed<AxisTick[]>(() => {
    const box = this.box();
    const band = this.band();
    return this.ordered().map((row, index) => ({
      key: row.subjectId,
      x: round(bandCentre(box, band, index)),
      y: round(box.bottom + 16),
      label: row.label,
      anchor: 'middle' as const,
    }));
  });

  readonly barViews = computed<ComparisonBarView[]>(() => {
    const box = this.box();
    const band = this.band();
    if (band === 0) return [];

    const colors = this.seriesColors();
    const groupWidth = band * GROUP_FILL;
    const barWidth = Math.min(MAX_BAR_WIDTH, (groupWidth - BAR_GAP) / 2);
    const groupSpan = barWidth * 2 + BAR_GAP;
    const bars: ComparisonBarView[] = [];

    this.ordered().forEach((row, index) => {
      const centre = bandCentre(box, band, index);
      const start = centre - groupSpan / 2;
      /* Une matière sans objectif n'apporte que la barre de l'élève ; sa place
         dans le groupe reste la même, pour que les barres « élève » restent
         alignées d'une matière à l'autre. */
      const values: readonly [ComparisonSeries, number | null][] = [
        ['student', row.student],
        ['target', row.target],
      ];

      values.forEach(([series, value], slot) => {
        if (value === null) return;
        const x = start + slot * (barWidth + BAR_GAP);
        const y = this.yFor(value);
        bars.push({
          key: `${row.subjectId}-${series}`,
          subjectId: row.subjectId,
          series,
          value,
          d: topRoundedBar(x, y, barWidth, box.bottom - y, 4),
          fill: colors[series],
        });
      });
    });

    return bars;
  });

  enter(subjectId: number, event: MouseEvent): void {
    const row = this.rows().find((candidate) => candidate.subjectId === subjectId);
    if (!row) return;

    const colors = this.seriesColors();
    const labels = this.seriesLabels();
    const format = this.percentFormat();
    const items = [
      { label: labels.student, value: format(row.student), color: colors.student },
    ];
    if (row.target !== null) {
      items.push({ label: labels.target, value: format(row.target), color: colors.target });
    }
    this.tooltip.show(row.label, items, event);
  }
}
